/**
 * 批次汇总 Tab — 航班×产品矩阵
 *
 * 设计文档: docs/05_workbench_architecture_v2.md
 *
 * 数据来源：全部从 BatchContext 获取（统一数据中心），不自行加载。
 *
 * 子组件（同目录）：
 *   - AircraftGroupPanel: 飞机分组面板（航班×产品矩阵表格）
 *   - UnmatchedChargesPanel: 未匹配费用面板
 *   - DetailDrawer: 航班费用详情抽屉
 */
import React, { useState, useMemo, useCallback } from "react";
import { Tabs, Space, Tag, Spin, Empty, Alert, Button, Typography } from "antd";
import { WarningOutlined } from "@ant-design/icons";

import { useBatchContext } from "../../context/BatchContext";
import AircraftGroupPanel from "./AircraftGroupPanel";
import { UnmatchedChargesPanel } from "./UnmatchedChargesPanel";
import DetailDrawer from "./DetailDrawer";
import AircraftTag from "../../../../../components/AircraftTag";
import { isConversionWarning } from "../../utils/invoiceBalanceDisplay";

import "./summary.css";

const { Text } = Typography;

const BatchSummaryTab = ({ onInvoiceClick }) => {
    const {
        // IDS 数据
        eligibleIds,
        loading: idsLoading,
        // 矩阵数据
        matrixData,
        matrixLoading,
        matrixError,
        // 验证
        validationMap,
        // 账单标识
        invoiceMap,
        // 便捷引用
        products,
        summary,
        aircraftGroups,
        unmatchedCharges,
        // 操作
        refreshMatrix,
    } = useBatchContext();

    // Tab 状态（按飞机分组）
    const [activeTabKey, setActiveTabKey] = useState(null);

    // 详情抽屉
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [selectedFlight, setSelectedFlight] = useState(null);

    const handleFlightClick = useCallback((flight, focusProduct = null) => {
        setSelectedFlight({ ...flight, focusProduct });
        setDrawerVisible(true);
    }, []);

    const handleDrawerClose = useCallback(() => {
        setDrawerVisible(false);
        setSelectedFlight(null);
    }, []);

    // 选中航班的验证详情
    const selectedFlightValidation = selectedFlight ? validationMap[selectedFlight.flight_id] : null;
    const amountWarnings = (summary?.warnings || matrixData?.warnings || []).filter(isConversionWarning);

    // 构建 Tabs
    const tabItems = useMemo(() => {
        if (!matrixData) return [];

        const items = [];

        // 未匹配费用 Tab（如果有的话，放在最前面）
        if (unmatchedCharges.length > 0) {
            items.push({
                key: "__unmatched__",
                label: (
                    <span style={{ color: "#faad14" }}>
                        <WarningOutlined style={{ marginRight: 4 }} />
                        未匹配费用
                        <span style={{ fontSize: 12, marginLeft: 6 }}>
                            ({unmatchedCharges.length})
                        </span>
                    </span>
                ),
                children: (
                    <UnmatchedChargesPanel
                        charges={unmatchedCharges}
                        currency={summary?.currency}
                        products={products}
                        invoiceMap={invoiceMap}
                        onInvoiceClick={onInvoiceClick}
                    />
                ),
            });
        }

        // 飞机分组 Tabs
        aircraftGroups.forEach((group) => {
            // 计算该组的验证统计（存在问题的航班数）
            let issueCount = 0;
            let executionFailureCount = 0;
            (group.flights || []).forEach((f) => {
                const fv = validationMap[f.flight_id];
                if (fv?.executionFailed || fv?.status === "EXECUTION_FAILED") executionFailureCount++;
                if (fv?.businessValidationStatus === "HAS_ISSUES" || (!fv?.executionFailed && fv?.status === "HAS_ISSUES")) issueCount++;
            });

            items.push({
                key: group.aircraft_reg,
                label: (
                    <Space size={6}>
                        <AircraftTag registration={group.aircraft_reg} />
                        <span style={{ color: "#999", fontSize: 12 }}>
                            ({group.charged_flight_count}航班)
                        </span>
                        {issueCount > 0 && (
                            <Tag color="red" style={{ fontSize: 10, padding: "0 4px", margin: 0 }}>
                                {issueCount}警告
                            </Tag>
                        )}
                        {executionFailureCount > 0 && (
                            <Tag color="orange" style={{ fontSize: 10, padding: "0 4px", margin: 0 }}>
                                {executionFailureCount}执行失败
                            </Tag>
                        )}
                    </Space>
                ),
                children: (
                    <AircraftGroupPanel
                        group={group}
                        products={products}
                        currency={summary?.currency}
                        relatedAirport={matrixData?.context?.related_airport}
                        onFlightClick={handleFlightClick}
                        validationMap={validationMap}
                    />
                ),
            });
        });

        return items;
    }, [matrixData, aircraftGroups, unmatchedCharges, products, summary, validationMap, handleFlightClick]);

    // 设置默认 Tab
    React.useEffect(() => {
        if (tabItems.length > 0 && !activeTabKey) {
            setActiveTabKey(tabItems[0].key);
        }
    }, [tabItems, activeTabKey]);

    // ---- 提前返回 ----

    if (eligibleIds.length === 0 && !idsLoading) {
        return (
            <div style={{ padding: 24 }}>
                <Empty
                    description={
                        <Space direction="vertical" align="center">
                            <Text type="secondary">暂无可汇总的账单数据</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                账单需至少完成"分摊"阶段后，才能进入双源批次汇总窗口
                            </Text>
                        </Space>
                    }
                />
            </div>
        );
    }

    if (matrixLoading || idsLoading) {
        return (
            <div style={{ textAlign: "center", padding: 60 }}>
                <Spin tip="加载批次汇总数据..." />
            </div>
        );
    }

    if (matrixError) {
        return (
            <Alert
                type="error"
                message="矩阵数据加载失败"
                description={matrixError}
                action={<Button onClick={refreshMatrix}>重试</Button>}
                style={{ margin: 16 }}
            />
        );
    }

    if (!tabItems.length) {
        return (
            <div style={{ padding: 40, textAlign: "center" }}>
                <Empty description="暂无对账数据" />
            </div>
        );
    }

    return (
        <div>
            {amountWarnings.length > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    message="批次汇总存在金额折算提示"
                    description={
                        <div style={{ whiteSpace: "pre-line" }}>
                            {amountWarnings.slice(0, 5).map((warning) => warning.message || warning.code).join("\n")}
                            {amountWarnings.length > 5 ? `\n另有 ${amountWarnings.length - 5} 条提示` : ""}
                        </div>
                    }
                    style={{ margin: "0 0 12px" }}
                />
            )}
            <Tabs
                activeKey={activeTabKey}
                onChange={setActiveTabKey}
                items={tabItems}
                type="card"
                className="aircraft-tabs"
                size="small"
            />

            {/* 航班费用详情抽屉 */}
            <DetailDrawer
                visible={drawerVisible}
                flight={selectedFlight}
                products={products}
                invoiceMap={invoiceMap}
                onClose={handleDrawerClose}
                flightValidation={selectedFlightValidation}
                onInvoiceClick={onInvoiceClick}
            />
        </div>
    );
};

export default BatchSummaryTab;
